import { DEFAULT_SELECT_PAGE_SIZE, TAG_TYPE_T1_HEADER } from '@common/constants';
import {
  Label,
  Form,
  requiredRule,
  UseFormReturn,
  FormSelect,
  FormSelectPaginate,
  IOnSearchParams,
  FormTextField,
} from '@gcl/react-core';
import { createTagFormKeys, ITagTypeOption } from '@tag/tag.models';
import { useFetchAllTagTypeOptions } from '@tag/tag.queries';
import React, { useEffect } from 'react';
import { useFetchProjectMasterScheduleTagSearch } from '@masterSchedule/masterScheduleTag/masterScheduleTag.queries';
import { useProjectId } from '@config/config.hooks';
import {
  IProjectMasterScheduleTagBase,
  IProjectMasterScheduleTagsSearchRequest,
} from '@masterSchedule/masterScheduleTag/masterScheduleTag.models';
import { AxiosErrorResponse } from '@core/error.models';
import { UseQueryResult } from '@tanstack/react-query';
import { ITagViewItem } from './TagView';

interface IAddTagFormProps {
  useFormMethods: UseFormReturn;
  existingTags: ITagViewItem[];
}
export const AddTagForm = ({ useFormMethods, existingTags }: IAddTagFormProps) => {
  const projectId = useProjectId();
  const {
    formState: { isDirty },
  } = useFormMethods;
  const tagType = useFormMethods.watch(createTagFormKeys.TAG_TYPE);
  const tagName = useFormMethods.watch(createTagFormKeys.TAG_NAME);

  const [localFilter, setLocalFilter] = React.useState<IProjectMasterScheduleTagsSearchRequest>({ projectId });

  const { data: dataTagTypeOptions } = (
    useFetchAllTagTypeOptions({ isDirty }) as () => UseQueryResult<ITagTypeOption[], AxiosErrorResponse>
  )();

  const { data: dataT1HeaderOptions, isLoading: isLoadingT1HeaderOptions } = useFetchProjectMasterScheduleTagSearch(
    localFilter,
    tagType?.name === TAG_TYPE_T1_HEADER,
  );

  useEffect(() => {
    if (tagType?.name === TAG_TYPE_T1_HEADER && tagName !== localFilter.text) {
      useFormMethods.setValue(createTagFormKeys.TAG_NAME, localFilter.text);
    }
  }, [localFilter.text]);

  useEffect(() => {
    const found = existingTags.find((tag) => tag.tagTypeName === TAG_TYPE_T1_HEADER);
    if (found && tagType?.name === TAG_TYPE_T1_HEADER)
      useFormMethods.setError(createTagFormKeys.TAG_TYPE, { message: 'Cannot create multiple T1 Header tag' });
    else useFormMethods.clearErrors(createTagFormKeys.TAG_TYPE);
  }, [JSON.stringify(tagType)]);

  useEffect(() => {
    return () => setLocalFilter({ projectId });
  }, []);

  useEffect(() => {
    if (tagType?.name !== TAG_TYPE_T1_HEADER) return;
    if (typeof tagName === 'object') {
      useFormMethods.setValue(createTagFormKeys.TAG_NAME, (tagName as IProjectMasterScheduleTagBase).name);
    }
  }, [JSON.stringify(tagType), JSON.stringify(tagName)]);

  return (
    <Form
      useFormMethods={useFormMethods}
      render={(formFieldMethods) => {
        return (
          <div>
            <Label labelFor={createTagFormKeys.TAG_TYPE}>Type</Label>
            <FormSelect
              options={dataTagTypeOptions || []}
              formFieldMethods={formFieldMethods}
              displayFieldName="name"
              id={createTagFormKeys.TAG_TYPE}
              placeholder="Select tag type..."
              rules={{
                ...requiredRule,
                validate: (value: any) => {
                  const found = existingTags.find((tag) => tag.tagTypeName === TAG_TYPE_T1_HEADER);
                  if (found && value.name === TAG_TYPE_T1_HEADER) return 'Cannot create multiple T1 Header tag';
                  return true;
                },
              }}
            />
            <Label labelFor={createTagFormKeys.TAG_NAME} required>
              Name
            </Label>
            {tagType?.name !== TAG_TYPE_T1_HEADER && (
              <FormTextField formFieldMethods={formFieldMethods} id={createTagFormKeys.TAG_NAME} rules={requiredRule} />
            )}
            {tagType?.name === TAG_TYPE_T1_HEADER && (
              <FormSelectPaginate
                options={dataT1HeaderOptions ?? []}
                isLoading={isLoadingT1HeaderOptions}
                shouldDebounce
                pageSize={DEFAULT_SELECT_PAGE_SIZE}
                rules={{
                  validate: (v: any) => {
                    if (v) return true;
                    return requiredRule.required.message;
                  },
                }}
                displayFieldName="name"
                formFieldMethods={formFieldMethods}
                id={createTagFormKeys.TAG_NAME}
                onSearch={(filter: IOnSearchParams) =>
                  setLocalFilter((old) => {
                    return { ...old, text: filter.keyword };
                  })
                }
              />
            )}
          </div>
        );
      }}
    />
  );
};
